import React, { useState } from "react";

import momentjs from "moment";
import { useIntl } from "react-intl";

import ContentLayout from "Components/ContentLayout";
import { BackLink, Link } from "ds/Button";
import SettingLine from "ds/SettingLine";
import useDecodedParams from "Hooks/useDecodedParams";
import { selectFeatureUsageBasedBillingEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { useLoadOrder } from "Reducers/organization/order/hooks/useLoadOrder";
import Invoice from "src/organization/pages/billing/components/Invoice/Invoice";
import Layout from "src/organization/pages/billing/components/Layout";
import OrderDetail from "src/organization/pages/billing/components/OrderDetail";
import ProjectDetail from "src/organization/pages/billing/components/ProjectDetail";
import OrderProjectList from "src/organization/pages/billing/history/detail/components/OrderProjectList";
import { useAppSelector } from "Store/hooks";

import { OrderHeader } from "./OrderHeader";
import {
  DetailLayout,
  OrderDetailLayout,
  HeaderLayout,
  OrderLayout,
  Title,
  OrganizationCostsTitle,
  OrgSectionText
} from "./styles";

import type { OrderLineItem } from "@packages/client";

const OrganizationBillingOrderDetails = () => {
  const { organizationId, orderId } = useDecodedParams<{
    organizationId: string;
    orderId: string;
  }>();
  const intl = useIntl();

  const { order, loading } = useLoadOrder({
    organizationId,
    orderId
  });

  const [expandedItem, setExpandedItem] = useState(null);

  const expand = id => {
    expandedItem === id ? setExpandedItem(null) : setExpandedItem(id);
  };

  const billingCycle = date => momentjs(date).format("MMMM YYYY");

  const organizationItems = order?.line_items?.filter(
    item => item.type === "organization_feature"
  );

  const usageBasedPagesEnabled = useAppSelector(
    selectFeatureUsageBasedBillingEnabled
  );

  return !loading && order ? (
    <Layout>
      <DetailLayout>
        <OrderDetailLayout>
          <ContentLayout id="settings-billing" className="settings-content">
            <BackLink
              from="invoices"
              to={`/${organizationId}/-/billing/history`}
              capitalized={false}
            />
            {usageBasedPagesEnabled ? (
              <Invoice organizationId={organizationId} orderId={orderId} />
            ) : (
              <>
                <HeaderLayout>
                  <Title tag="h2">{orderId}</Title>
                  {order.invoice_url && (
                    <Link
                      analyticId="download.pdf"
                      variant="primary"
                      target="_blank"
                      href={order.invoice_url}
                    >
                      {intl.formatMessage({ id: "download.pdf" })}
                    </Link>
                  )}
                </HeaderLayout>
                <OrderLayout>
                  <OrderHeader order={order} />
                  <OrderDetail
                    loading={loading}
                    order={order}
                    billingCycle={billingCycle(order?.billing_period_end)}
                  />
                </OrderLayout>
                <OrganizationCosts
                  items={organizationItems}
                  organizationId={organizationId}
                />
                <OrderProjectList
                  order={order}
                  title={intl.formatMessage({
                    id: "invoice.included_project_title"
                  })}
                  expandedItem={expandedItem}
                  expand={expand}
                  organizationId={organizationId}
                />
              </>
            )}
          </ContentLayout>
        </OrderDetailLayout>
      </DetailLayout>
    </Layout>
  ) : null;
};

const OrganizationCosts = ({
  items,
  organizationId
}: {
  items?: OrderLineItem[];
  organizationId: string;
}) => {
  const intl = useIntl();

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <>
      <OrganizationCostsTitle tag="h3">
        {intl.formatMessage({ id: "organization" })}
      </OrganizationCostsTitle>
      <SettingLine
        key="organization-costs"
        info={<OrgSectionText weight="bold">{organizationId}</OrgSectionText>}
        noToggle={true}
      >
        <ProjectDetail subscriptionId={organizationId} items={items} />
      </SettingLine>
    </>
  );
};

export default OrganizationBillingOrderDetails;
